import { isMapFeatureFlagEnabled } from '@helpers/events';
import type { IEvent } from '@interfaces/event';
import type {
  IBrandingEntity,
  IBrandingProperty,
  IBrandingType,
  ILiveBrandingEntity,
  IMap,
} from '@interfaces/map';
import type { IBrandingEntityValues } from '@interfaces/rooms';
import type { IBrandingEntitiesPayload } from '@interfaces/webSocketConnectionInfo';

/** Map feature flag the whole entity editor hangs off; enabled unless an experience turns it off. */
export const UNIVERSAL_BRANDING_FLAG_SLUG = 'UNIVERSAL_BRANDING_UE';

/**
 * Map feature flag the runtime half hangs off (MV-1905), on top of the flag
 * above rather than instead of it — the same pair UE gates its own reporting
 * on, and with the same asymmetry: with the flag above off, this one cannot
 * turn the runtime half back on.
 *
 * Ships **on**, like the flag above, so a map that does not declare the slug
 * reads as on and no fallback is passed. UE's own catalogue still defaults it
 * off, but the value arrives over `FEATURE_FLAGS` and the payload wins — so
 * what the map declares is what the client obeys, and an experience turning it
 * off is the only gate.
 */
export const DYNAMIC_BRANDING_FLAG_SLUG = 'DYNAMIC_BRANDING_UE';

/**
 * Control a property's declared `type` asks for.
 *
 * `unsupported` is the answer for a type this hub version has no widget for —
 * a newer build may declare one, and guessing a widget would write a value UE
 * then refuses.
 */
export type BrandingPropertyWidget =
  | 'colorPicker'
  | 'numberField'
  | 'integerField'
  | 'toggle'
  | 'textField'
  | 'urlField'
  | 'pathField'
  | 'select'
  | 'unsupported';

/** Why a value cannot be saved; the row maps this to a message. */
export type BrandingValueErrorCode =
  | 'color'
  | 'number'
  | 'integer'
  | 'boolean'
  | 'enum'
  | 'assetPath'
  | 'textureUrl';

const COLOR_PATTERN = /^#?([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const DECIMAL_PATTERN = /^[+-]?(\d+(\.\d*)?|\.\d+)$/;
const INTEGER_PATTERN = /^[+-]?\d+$/;
const HTTP_URL_PATTERN = /^https?:\/\/\S+$/i;

/**
 * Picks the control for a catalogue property.
 *
 * `assetPath` is a closed set only when the build declared one, so it resolves
 * to a dropdown or a free path field depending on `allowedValues`.
 *
 * @param property - Catalogue property being rendered.
 * @returns The widget to render, or `unsupported` for an unknown type.
 */
export const resolveBrandingPropertyWidget = (
  property: Pick<IBrandingProperty, 'type' | 'allowedValues'>,
): BrandingPropertyWidget => {
  switch (property.type) {
    case 'color':
      return 'colorPicker';
    case 'number':
      return 'numberField';
    case 'integer':
      return 'integerField';
    case 'boolean':
      return 'toggle';
    case 'text':
      return 'textField';
    case 'textureUrl':
      return 'urlField';
    case 'enum':
      return 'select';
    case 'assetPath':
      return property.allowedValues && property.allowedValues.length > 0
        ? 'select'
        : 'pathField';
    default:
      return 'unsupported';
  }
};

/**
 * Expands a hex colour to the canonical form UE parses.
 *
 * Accepts `#RGB`, `#RGBA`, `RRGGBB`, `#RRGGBB` and `#RRGGBBAA`, with or without
 * the leading `#`; shorthand digits are doubled, alpha is kept when given.
 *
 * @param raw - Value as typed or emitted by the picker.
 * @returns The normalized `#RRGGBB` / `#RRGGBBAA` value, or `null` when unparseable.
 */
export const normalizeBrandingColorValue = (raw: string): string | null => {
  const trimmed = raw.trim();
  const match = COLOR_PATTERN.exec(trimmed);
  if (!match) {
    return null;
  }

  const digits = match[1];
  if (digits.length === 3 || digits.length === 4) {
    return `#${digits
      .split('')
      .map((digit) => `${digit}${digit}`)
      .join('')}`;
  }

  return `#${digits}`;
};

/**
 * Checks a value against what the property's type accepts.
 *
 * An empty value is never an error — it means the property is cleared, which
 * restores what the level authored.
 *
 * @param property - Catalogue property the value belongs to.
 * @param value - Value as held by the editor.
 * @returns The failing rule, or `null` when the value is safe to save.
 */
export const validateBrandingPropertyValue = (
  property: Pick<IBrandingProperty, 'type' | 'allowedValues'>,
  value: string,
): BrandingValueErrorCode | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  switch (property.type) {
    case 'color':
      return normalizeBrandingColorValue(trimmed) ? null : 'color';
    case 'number':
      return DECIMAL_PATTERN.test(trimmed) ? null : 'number';
    case 'integer':
      // A fractional value is refused by UE per property, so it is reported
      // rather than rounded — rounding would silently save a different value.
      return INTEGER_PATTERN.test(trimmed) ? null : 'integer';
    case 'boolean':
      return trimmed === 'true' || trimmed === 'false' ? null : 'boolean';
    case 'enum':
      return (property.allowedValues ?? []).includes(trimmed) ? null : 'enum';
    case 'textureUrl':
      return HTTP_URL_PATTERN.test(trimmed) ? null : 'textureUrl';
    case 'assetPath':
      if (property.allowedValues && property.allowedValues.length > 0) {
        return property.allowedValues.includes(trimmed) ? null : 'assetPath';
      }
      return trimmed.startsWith('/') ? null : 'assetPath';
    default:
      // An unsupported type is rendered read-only, so nothing can be invalid.
      return null;
  }
};

/**
 * Normalizes the value the editor holds into the one that gets stored.
 *
 * @param property - Catalogue property the value belongs to.
 * @param value - Value as held by the editor.
 * @returns The value to store, or `''` when the property is cleared.
 */
export const toStoredBrandingValue = (
  property: Pick<IBrandingProperty, 'type' | 'allowedValues'>,
  value: string,
): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  if (property.type === 'color') {
    return normalizeBrandingColorValue(trimmed) ?? trimmed;
  }

  return trimmed;
};

/**
 * Builds the `brandingEntities` body for one entity.
 *
 * The backend replaces the whole property map of every entity it is given, so
 * the complete set of stored values is sent — a cleared property is omitted,
 * which is what restores the authored value.
 *
 * @param entityId - Entity being saved.
 * @param values - Every property the editor holds for that entity.
 * @returns The request body fragment, carrying that entity alone.
 */
export const buildBrandingEntityValues = (
  entityId: string,
  values: Record<string, string>,
): IBrandingEntityValues => {
  const properties: Record<string, string> = {};

  Object.entries(values).forEach(([propertyId, value]) => {
    if (value) {
      properties[propertyId] = value;
    }
  });

  return { [entityId]: properties };
};

/**
 * Builds the single payload that moves UE from one configuration to another.
 *
 * Every entity the outgoing configuration overrode but the incoming one does
 * not is sent as an empty map, which restores the authored values — per-entity
 * replace cannot unset an entity the payload never names.
 *
 * @param previousValues - Values of the configuration being left.
 * @param nextValues - Values of the configuration being activated.
 * @returns The union to send as one `BRANDING_UPDATED`.
 */
export const buildBrandingSwitchEntities = (
  previousValues: IBrandingEntityValues | undefined,
  nextValues: IBrandingEntityValues | undefined,
): IBrandingEntityValues => {
  const switchEntities: IBrandingEntityValues = {};

  Object.keys(previousValues ?? {}).forEach((entityId) => {
    switchEntities[entityId] = {};
  });

  Object.entries(nextValues ?? {}).forEach(([entityId, properties]) => {
    switchEntities[entityId] = properties;
  });

  return switchEntities;
};

/**
 * A row of the objects panel, from whichever source it came.
 *
 * `IBrandingEntity` is the shape the renderer already takes, so a live object is
 * projected onto it rather than given a second one — the property object is
 * identical in both sources, which is the whole point of the class schema.
 */
export interface IBrandingEntityCatalogueEntry extends IBrandingEntity {
  /** Unreal reports this object standing in the world right now. */
  isLive: boolean;
  /**
   * What every property's `default` on this row means, and therefore what its
   * reset restores: `instance` is the value the placed actor was authored with
   * in its level, `class` the value the class itself ships. The two are the same
   * field in the same shape, and only the source tells them apart.
   */
  defaultSource: 'instance' | 'class';
  /**
   * Present only on a live object a placement re-keyed. Diagnostics: its
   * absence means the object answers to the id its asset authored, which in
   * practice means it was placed in a map rather than spawned.
   */
  authoredId?: string;
}

/** Where the objects panel assembles its rows from. */
export interface IBrandingEntitySources {
  /** The map record: what the build declared, with per-instance defaults. */
  map?: IMap | null;
  /** What Unreal reports standing right now (BRANDING_ENTITIES). */
  liveEntities?: ILiveBrandingEntity[] | null;
  /** Class schemas from the streamed build's record, carrying class defaults. */
  brandingTypes?: IBrandingType[] | null;
  /**
   * `clientId` of the streamed build the schemas came off. Only used to scope
   * the missing-schema warning, so that entering another experience on another
   * build reports its own gaps rather than staying silent about a class this tab
   * has already complained about.
   */
  buildId?: string | null;
}

// (buildId, entityType) pairs already reported missing from `brandingTypes`. A
// live object whose class is not in the export is a known UE gap — a brandable
// Blueprint reachable only through a runtime catalog, never placed in a shipped
// map — so it is dropped rather than shown half-editable, but it is worth saying
// once: the class has to be named in a ticket before it can be added.
const warnedMissingEntityTypes = new Set<string>();

/**
 * Forgets which missing schemas have already been warned about.
 *
 * Exists for the tests: the set is module-level, so without it one test's
 * warning silences the next one's and the file only passes in a given order.
 *
 * @returns Nothing.
 */
export const resetBrandingSchemaWarnings = (): void => {
  warnedMissingEntityTypes.clear();
};

/**
 * Reads the entities out of a BRANDING_ENTITIES frame.
 *
 * Rows without a `brandingId` are dropped: it is the only key values are saved
 * against, so a row missing it is not addressable. Duplicates are left alone —
 * Unreal drops them before sending, and the catalogue de-duplicates anyway.
 *
 * @param message - The frame as received; the backend relays it flat, so
 * `entities` rides on its root.
 * @returns The reported entities, empty when the frame carries none.
 */
export const parseLiveBrandingEntities = (
  message:
    | IBrandingEntitiesPayload
    | Record<string, unknown>
    | null
    | undefined,
): ILiveBrandingEntity[] => {
  const raw = (message as { entities?: unknown } | null | undefined)?.entities;

  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.reduce<ILiveBrandingEntity[]>((entities, row) => {
    const entity = row as Partial<ILiveBrandingEntity> | null;
    if (typeof entity?.brandingId !== 'string' || !entity.brandingId) {
      return entities;
    }

    return [
      ...entities,
      {
        brandingId: entity.brandingId,
        entityType:
          typeof entity.entityType === 'string' ? entity.entityType : '',
        displayName:
          typeof entity.displayName === 'string' ? entity.displayName : '',
        ...(typeof entity.authoredId === 'string' && entity.authoredId
          ? { authoredId: entity.authoredId }
          : {}),
      },
    ];
  }, []);
};

/**
 * Tells whether two reports describe the same standing world.
 *
 * Unreal re-reports on every level add and remove, so the same list arrives
 * again whenever a room is streamed in that carries nothing brandable. Holding
 * the previous array in that case keeps the context value of
 * `MytaverseEventProvider` — a fresh object literal on every render — from
 * re-rendering every consumer of `useMytaverseEvent`, the video frame included.
 * A field-by-field comparison is enough: `SortForReport` fixes the order, so a
 * pairwise walk cannot report a reordering as a change.
 *
 * @param left - Report held today; `null` before Unreal has said anything.
 * @param right - Report just parsed.
 * @returns Whether the two carry the same entities in the same order.
 */
export const areLiveBrandingEntitiesEqual = (
  left: ILiveBrandingEntity[] | null | undefined,
  right: ILiveBrandingEntity[] | null | undefined,
): boolean => {
  if (!left || !right) {
    // Null is "Unreal has not said", which an empty report is not.
    return left === right;
  }

  if (left.length !== right.length) {
    return false;
  }

  return left.every((entity, index) => {
    const other = right[index];

    return (
      entity.brandingId === other.brandingId &&
      entity.entityType === other.entityType &&
      entity.displayName === other.displayName &&
      entity.authoredId === other.authoredId
    );
  });
};

/**
 * Joins the brandable objects of both sources into the one list the panel shows.
 *
 * The map record is what the build declared and carries the values each *placed*
 * actor was authored with; the live report is what is standing right now and
 * carries nothing but ids, so its properties come from the class schema. The two
 * overlap by design — a placed object is reported too, it simply has no
 * `authoredId` — so the map record wins on a shared `brandingId`: its defaults
 * describe that instance, where the class's describe every instance.
 *
 * A live object whose `entityType` is in no schema is skipped, since there is
 * nothing to render for it.
 *
 * @param sources - The map record, the live report, and the streamed build's
 * class schemas and id.
 * @returns One row per addressable object, map entries first.
 */
export const getBrandingEntityCatalogue = (
  sources: IBrandingEntitySources,
): IBrandingEntityCatalogueEntry[] => {
  const { map, liveEntities, brandingTypes, buildId } = sources;

  const declared = Array.isArray(map?.brandingEntities)
    ? (map?.brandingEntities ?? []).filter((entity) => !!entity?.entityId)
    : [];
  const reported = Array.isArray(liveEntities) ? liveEntities : [];

  // Which of the declared entries Unreal also reports standing. Read before the
  // rows are built so a map entry can carry the liveness of its live twin.
  const liveIds = new Set(reported.map((entity) => entity.brandingId));

  const entries: IBrandingEntityCatalogueEntry[] = declared.map((entity) => ({
    ...entity,
    isLive: liveIds.has(entity.entityId),
    defaultSource: 'instance',
  }));

  const schemasByType = new Map(
    (Array.isArray(brandingTypes) ? brandingTypes : [])
      .filter((type) => !!type?.entityType)
      .map((type) => [type.entityType, type]),
  );
  const declaredIds = new Set(declared.map((entity) => entity.entityId));

  reported.forEach((entity) => {
    if (declaredIds.has(entity.brandingId)) {
      return;
    }

    const schema = schemasByType.get(entity.entityType);
    if (!schema) {
      // Scoped to the build, since the gap is a property of the build's export:
      // the same class may well be exported by the next one, and a set keyed by
      // class alone would stay quiet about it for the rest of the tab's life.
      const warningKey = `${buildId ?? ''} ${entity.entityType}`;

      if (!warnedMissingEntityTypes.has(warningKey)) {
        warnedMissingEntityTypes.add(warningKey);
        console.warn(
          'No brandable class schema for a live entity; skipping it',
          {
            entityType: entity.entityType,
            brandingId: entity.brandingId,
            mapId: map?.mapId,
            buildId,
          },
        );
      }

      return;
    }

    // Deliberately not de-duplicated against the rows already pushed: Unreal
    // guarantees one row per brandingId in a report, and `declaredIds` covers
    // the overlap with the map record.
    entries.push({
      entityId: entity.brandingId,
      displayName: entity.displayName || schema.displayName || '',
      entityType: entity.entityType,
      // A spawned object is in no level of the build, so it belongs to no map.
      maps: [],
      properties: schema.properties ?? [],
      isLive: true,
      defaultSource: 'class',
      ...(entity.authoredId ? { authoredId: entity.authoredId } : {}),
    });
  });

  return entries;
};

/**
 * Tells whether the runtime half of the object editor is on for this room.
 *
 * Both flags, exactly as Unreal gates its own reporting: the runtime half is an
 * extension of universal branding, not an alternative to it.
 *
 * @param currentEvent - Experience holding the per-map flag overrides.
 * @param map - Map of the room being edited.
 * @returns Whether live objects may be listed and edited.
 */
export const isDynamicBrandingEnabled = (
  currentEvent: IEvent | null | undefined,
  map: IMap | null | undefined,
): boolean =>
  isMapFeatureFlagEnabled(currentEvent, map, UNIVERSAL_BRANDING_FLAG_SLUG) &&
  isMapFeatureFlagEnabled(currentEvent, map, DYNAMIC_BRANDING_FLAG_SLUG);

/**
 * The objects panel's rows for the current room, with the flags applied.
 *
 * The gate is applied here rather than at each reader, so nothing can list a
 * live object on a room where the runtime half is off.
 *
 * @param currentEvent - Experience holding the per-map flag overrides.
 * @param sources - The map record, the live report, and the build's class schemas.
 * @returns The rows to show, live ones included only when both flags are on.
 */
export const resolveBrandingEntityCatalogue = (
  currentEvent: IEvent | null | undefined,
  sources: IBrandingEntitySources,
): IBrandingEntityCatalogueEntry[] =>
  getBrandingEntityCatalogue({
    ...sources,
    liveEntities: isDynamicBrandingEnabled(currentEvent, sources.map)
      ? sources.liveEntities
      : null,
  });

/**
 * Tells whether the entity editor may be shown for the current room.
 *
 * "Something to brand" counts what is standing as well as what the build
 * declared: a map that places no brandable object can still have rooms streamed
 * into it that carry one, and gating on the declared list alone hid the panel on
 * exactly those maps.
 *
 * @param currentEvent - Experience holding the per-map flag overrides.
 * @param sources - The map record, the live report, and the build's class schemas.
 * @returns Whether the flag is on and there is something to brand.
 */
export const isBrandingEntityEditorAvailable = (
  currentEvent: IEvent | null | undefined,
  sources: IBrandingEntitySources,
): boolean => {
  if (resolveBrandingEntityCatalogue(currentEvent, sources).length === 0) {
    return false;
  }

  return isMapFeatureFlagEnabled(
    currentEvent,
    sources.map,
    UNIVERSAL_BRANDING_FLAG_SLUG,
  );
};
